// File: src/MathTools/CubicGraphTool.js
//----------------------------------------------------------
// A React component to graph a cubic-like function, with axis ticks.
// Now includes a safeguard to limit max # of ticks on each axis.
function CubicGraphTool({
  equation = "x**3",
  xMin = -5,
  xMax = 5,
  step = 0.1,
  width = 400,
  height = 300,
  margin = 30,
  showAxes = true,
  showTicks = true,
  xTickStep = 1,
  yTickStep = 1,
  maxTicks = 30   // New prop to limit how many ticks we’ll draw
}) {
  // 1) Convert `equation` string into a function that computes f(x).
  // eslint-disable-next-line no-new-func
  const fn = new Function("x", `return ${equation};`);

  // 2) Generate plot points from xMin..xMax with given step.
  const points = [];
  for (let x = xMin; x <= xMax; x += step) {
    const yVal = fn(x);
    points.push({ x, y: yVal });
  }

  // 3) Determine min/max y from the points (for basic auto-scaling).
  let yMin = Infinity, yMaxi = -Infinity;
  points.forEach(pt => {
    if (pt.y < yMin) yMin = pt.y;
    if (pt.y > yMaxi) yMaxi = pt.y;
  });
  // Optional small padding
  const pad = 0.1 * (yMaxi - yMin);
  yMin -= pad;
  yMaxi += pad;

  // 4) The plotting region
  const usableWidth = width - 2 * margin;
  const usableHeight = height - 2 * margin;

  // Helpers to map real x,y to SVG
  function mapXtoSvg(xVal) {
    const ratio = (xVal - xMin) / (xMax - xMin);
    return margin + ratio * usableWidth;
  }
  function mapYtoSvg(yVal) {
    const ratio = (yVal - yMin) / (yMaxi - yMin);
    return margin + (1 - ratio) * usableHeight;
  }

  // 5) Build the path string
  if (!points.length) {
    return <div>No points to plot.</div>;
  }
  const pathD = points.map((pt, i) => {
    const sx = mapXtoSvg(pt.x);
    const sy = mapYtoSvg(pt.y);
    return i === 0 ? `M ${sx},${sy}` : `L ${sx},${sy}`;
  }).join(" ");

  // 6) Build tick arrays, but limit their size
  let xTicks = [];
  let yTicks = [];
  if (showTicks) {
    // X ticks
    const minXint = Math.ceil(xMin);
    const maxXint = Math.floor(xMax);
    for (let xv = minXint; xv <= maxXint; xv += xTickStep) {
      xTicks.push(xv);
    }
    // If too many, skip or scale
    if (xTicks.length > maxTicks) {
      xTicks = []; // or do a more clever approach
    }

    // Y ticks
    const minYint = Math.ceil(yMin);
    const maxYint = Math.floor(yMaxi);
    let possibleY = [];
    for (let yv = minYint; yv <= maxYint; yv += yTickStep) {
      possibleY.push(yv);
    }
    // If too many, skip or scale
    if (possibleY.length > maxTicks) {
      // Option 1: just skip
      // possibleY = [];
      // Option 2: pick a new step so the total ticks ~ maxTicks
      // e.g. let ratio = Math.ceil( possibleY.length / maxTicks );
      // then rebuild ticks in bigger increments
      const ratio = Math.ceil(possibleY.length / maxTicks);
      let newArr = [];
      for (let i = 0; i < possibleY.length; i += ratio) {
        newArr.push(possibleY[i]);
      }
      possibleY = newArr;
    }
    yTicks = possibleY;
  }

  // 7) Render the SVG
  return (
    <div style={{ textAlign: "center" }}>
      <svg width={width} height={height} style={{ border: "1px solid #ccc" }}>
        {/* Possibly draw X-axis if 0 is in y-range */}
        {showAxes && yMin < 0 && yMaxi > 0 && (
          <line
            x1={mapXtoSvg(xMin)}
            y1={mapYtoSvg(0)}
            x2={mapXtoSvg(xMax)}
            y2={mapYtoSvg(0)}
            stroke="gray"
            strokeWidth={1}
          />
        )}
        {/* Possibly draw Y-axis if 0 is in x-range */}
        {showAxes && xMin < 0 && xMax > 0 && (
          <line
            x1={mapXtoSvg(0)}
            y1={mapYtoSvg(yMin)}
            x2={mapXtoSvg(0)}
            y2={mapYtoSvg(yMaxi)}
            stroke="gray"
            strokeWidth={1}
          />
        )}

        {/* Draw X-axis Ticks */}
        {showAxes && showTicks && yMin < 0 && yMaxi > 0 && xTicks.map((xt, i) => {
          const sx = mapXtoSvg(xt);
          const sy0 = mapYtoSvg(0);
          return (
            <g key={`xtick-${i}`}>
              <line
                x1={sx}
                y1={sy0 - 5}
                x2={sx}
                y2={sy0 + 5}
                stroke="black"
                strokeWidth={1}
              />
              <text
                x={sx}
                y={sy0 + 18}
                textAnchor="middle"
                fontSize={12}
                fill="black"
              >
                {xt}
              </text>
            </g>
          );
        })}

        {/* Draw Y-axis Ticks */}
        {showAxes && showTicks && xMin < 0 && xMax > 0 && yTicks.map((yt, i) => {
          const sy = mapYtoSvg(yt);
          const sx0 = mapXtoSvg(0);
          return (
            <g key={`ytick-${i}`}>
              <line
                x1={sx0 - 5}
                y1={sy}
                x2={sx0 + 5}
                y2={sy}
                stroke="black"
                strokeWidth={1}
              />
              <text
                x={sx0 - 8}
                y={sy + 4}
                textAnchor="end"
                fontSize={12}
                fill="black"
              >
                {yt}
              </text>
            </g>
          );
        })}

        {/* The main cubic path */}
        <path
          d={pathD}
          fill="none"
          stroke="blue"
          strokeWidth={2}
        />
      </svg>
      <div style={{ fontSize: "0.9em", marginTop: 4 }}>
        <em>CubicGraphTool for equation: {equation}</em>
      </div>
    </div>
  );
}

export default CubicGraphTool;
